<template>
  <div class="page-wrapper">
    <HeaderComponent />

    <main class="page-content">
      <AppBreadcrumb :items="[{ label: $t('common.home') }]" />

      <section class="seo-section" :aria-label="$t('home.seoTitle')">
        <h1 class="sr-only">
          {{ $t('home.seoTitle') }}
        </h1>
        <p class="sr-only">
          {{ $t('home.seoDescription') }}
        </p>
      </section>

      <!-- Premium ogohlantirish -->
      <div v-if="!isPremium" class="premium-banner">
        <div class="premium-banner-content">
          <span class="premium-icon">💎</span>
          <div class="premium-text">
            <strong>{{ $t('home.premiumBannerTitle') }}</strong>
            <p>{{ $t('home.premiumBannerText') }}</p>
          </div>
          <a href="https://t.me/smartypx_bot" target="_blank" class="premium-btn">
            {{ $t('home.premiumBannerBtn') }}
          </a>
        </div>
      </div>

      <section class="categories-container" aria-labelledby="categories-title">
        <div class="categories-header">
          <h2 id="categories-title" class="section-title">{{ $t('home.sectionsTitle') }}</h2>
          <p class="section-subtitle">
            {{ $t('home.sectionsSubtitle') }}
          </p>
        </div>

        <nav class="categories-grid" :aria-label="$t('home.sectionsTitle')">
          <div
            v-for="(item, index) in categories"
            :key="item.key"
            class="category-item"
            :style="{ animationDelay: `${index * 0.05}s` }"
          >
            <router-link
              :to="item.disabled ? '#' : item.to"
              class="home-card"
              :class="{
                'is-disabled': item.disabled,
                [`card-accent-${item.accent}`]: true
              }"
              @click="handleCardClick($event, item)"
            >
              <div class="home-card-inner">
                <!-- Accent bar -->
                <div class="card-accent-bar"></div>

                <div class="card-top">
                  <div class="card-icon-box">
                    <component :is="item.iconComponent" class="card-svg-icon" />
                  </div>
                  <div v-if="item.badge" class="card-badge" :class="`badge-${item.badgeColor}`">
                    {{ item.badge }}
                  </div>
                </div>

                <div class="card-body">
                  <h3 class="card-title">{{ item.title }}</h3>
                  <p class="card-desc">{{ item.description }}</p>
                </div>

                <div v-if="!item.disabled" class="card-footer">
                  <span class="card-link-text">{{ $t('common.start') }}</span>
                  <span class="card-arrow">→</span>
                </div>
              </div>
            </router-link>
          </div>
        </nav>
      </section>

      <!-- Qulflangan bo'limlar uchun Premium Modal -->
      <a-modal v-model:visible="showPremiumModal" :title="$t('home.premiumBannerTitle')" :footer="null" centered>
        <div class="premium-modal-content">
          <p>{{ $t('home.premiumBannerText') }}</p>
          <a-button type="primary" href="https://t.me/smartypx_bot" target="_blank" block class="premium-btn-modal">
            {{ $t('home.premiumBannerBtn') }}
          </a-button>
        </div>
      </a-modal>
    </main>
  </div>
</template>

<script>
import { message } from 'ant-design-vue'
import HeaderComponent from '../components/HeaderComponent.vue'
import AppBreadcrumb from '../components/AppBreadcrumb.vue'
import {
  FormOutlined, OrderedListOutlined, ReadOutlined, AimOutlined,
  CloseCircleOutlined, WarningOutlined, NodeIndexOutlined,
  MedicineBoxOutlined, DollarOutlined, TrophyOutlined
} from '@ant-design/icons-vue'

export default {
  name: 'HomeView',
  components: {
    HeaderComponent, AppBreadcrumb,
    FormOutlined, OrderedListOutlined, ReadOutlined, AimOutlined,
    CloseCircleOutlined, WarningOutlined, NodeIndexOutlined,
    MedicineBoxOutlined, DollarOutlined, TrophyOutlined
  },
  data() {
    return {
      showPremiumModal: false
    }
  },
  computed: {
    isPremium() {
      return this.$store.getters['auth/isPremium']
    },
    categories() {
      const premium = this.isPremium
      return [
        {
          key: 'real-exam',
          title: this.$t('home.realExam'),
          description: this.$t('home.realExamDesc'),
          to: '/real-exam',
          iconComponent: 'FormOutlined',
          accent: 'blue'
        },
        {
          key: 'tickets-10',
          title: this.$t('home.tickets10'),
          description: this.$t('home.tickets10Desc'),
          to: '/tickets/10',
          iconComponent: 'OrderedListOutlined',
          accent: 'cyan',
          badge: !premium ? '🔒' : null,
          badgeColor: !premium ? 'red' : null,
          disabled: !premium,
          isPremiumLocked: !premium
        },
        {
          key: 'tickets-20',
          title: this.$t('home.tickets20'),
          description: this.$t('home.tickets20Desc'),
          to: '/tickets/20',
          iconComponent: 'ReadOutlined',
          accent: 'indigo',
          badge: !premium ? '🔒' : null,
          badgeColor: !premium ? 'red' : null,
          disabled: !premium,
          isPremiumLocked: !premium
        },
        {
          key: 'tickets-50',
          title: this.$t('home.tickets50'),
          description: this.$t('home.tickets50Desc'),
          to: '/tickets/50',
          iconComponent: 'AimOutlined',
          accent: 'violet',
          badge: !premium ? '🔒' : null,
          badgeColor: !premium ? 'red' : null,
          disabled: !premium,
          isPremiumLocked: !premium
        },
        {
          key: 'wrong-questions',
          title: this.$t('home.wrongQuestions'),
          description: this.$t('home.wrongQuestionsDesc'),
          to: '/wrong-questions',
          iconComponent: 'CloseCircleOutlined',
          accent: 'rose',
          badge: !premium ? '🔒' : null,
          badgeColor: !premium ? 'red' : null,
          disabled: !premium,
          isPremiumLocked: !premium
        },
        {
          key: 'challenge',
          title: 'Musobaqa',
          description: 'Do\'stlar bilan bellashing',
          to: '/challenge',
          iconComponent: 'TrophyOutlined',
          accent: 'amber'
        },
        {
          key: 'fine-points',
          title: this.$t('home.finePoints'),
          description: this.$t('home.finePointsDesc'),
          to: '/fine-points',
          iconComponent: 'WarningOutlined',
          accent: 'amber'
        },
        {
          key: 'road-signs',
          title: this.$t('home.roadSigns'),
          description: this.$t('home.roadSignsDesc'),
          to: '/road-signs',
          iconComponent: 'NodeIndexOutlined',
          accent: 'green',
          disabled: false
        },
        {
          key: 'medical-info',
          title: this.$t('home.medicalInfo'),
          description: this.$t('home.medicalInfoDesc'),
          to: '/medical-info',
          iconComponent: 'MedicineBoxOutlined',
          accent: 'teal',
          badge: this.$t('home.badgeInProgress'),
          badgeColor: 'orange',
          disabled: true
        },
        {
          key: 'fines',
          title: this.$t('home.fines'),
          description: this.$t('home.finesDesc'),
          to: '/fines',
          iconComponent: 'DollarOutlined',
          accent: 'orange',
          badge: this.$t('home.badgeInProgress'),
          badgeColor: 'orange',
          disabled: true
        }
      ]
    }
  },
  mounted() {
    // Premium statusni yangilash
    this.$store.dispatch('auth/fetchPremiumStatus')
  },
  methods: {
    handleCardClick(e, item) {
      if (item.disabled) {
        e.preventDefault()
        if (item.isPremiumLocked) {
          this.showPremiumModal = true
        } else {
          message.info(this.$t('home.sectionNotReady'))
        }
      }
    }
  }
}
</script>

<style scoped>
.premium-modal-content {
  text-align: center;
  padding: 20px 0;
}

.premium-modal-content p {
  margin-bottom: 24px;
  font-size: 15px;
  color: var(--text-soft);
}

.premium-btn-modal {
  height: 44px;
  background: linear-gradient(135deg, #ff6b35, #f7c948) !important;
  border: none !important;
  font-weight: 600;
  font-size: 15px;
}

.premium-btn-modal:hover {
  background: linear-gradient(135deg, #f7c948, #ff6b35) !important;
  box-shadow: 0 4px 12px rgba(255, 107, 53, 0.4) !important;
}

.page-wrapper {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: var(--bg);
}

.page-content {
  flex: 1;
  padding: 0 24px 32px;
  max-width: 1400px;
  margin: 0 auto;
  width: 100%;
}

.seo-section {
  margin: 0;
  padding: 0;
}

/* Premium banner */
.premium-banner {
  margin: 16px 0;
  background: linear-gradient(135deg, #ff6b35, #f7c948);
  border-radius: 16px;
  padding: 3px;
}

.premium-banner-content {
  display: flex;
  align-items: center;
  gap: 16px;
  background: var(--bg, #fff);
  border-radius: 14px;
  padding: 16px 20px;
}

.premium-icon {
  font-size: 32px;
  flex-shrink: 0;
}

.premium-text {
  flex: 1;
}

.premium-text strong {
  display: block;
  font-size: 15px;
  color: var(--text, #111);
  margin-bottom: 2px;
}

.premium-text p {
  margin: 0;
  font-size: 13px;
  color: var(--text-soft, #666);
}

.premium-btn {
  flex-shrink: 0;
  background: linear-gradient(135deg, #ff6b35, #f7c948);
  color: #fff;
  border: none;
  padding: 10px 20px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 13px;
  text-decoration: none;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
}

.premium-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(255, 107, 53, 0.4);
}

.categories-container {
  margin-top: 24px;
}

.categories-header {
  margin-bottom: 20px;
}

.section-title {
  margin: 0 0 6px;
  font-size: 24px;
  font-weight: 700;
  line-height: 1.3;
  color: var(--text, #111827);
}

.section-subtitle {
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: var(--text-soft, #6b7280);
}

/* ===== Cards Grid ===== */
.categories-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
}

.category-item {
  width: 100%;
  animation: cardFadeIn 0.45s ease both;
}

/* ===== NEW Card Design ===== */
.home-card {
  display: block;
  text-decoration: none;
  border-radius: 20px;
  overflow: hidden;
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  height: 100%;
}

.home-card:hover {
  transform: translateY(-4px);
}

.home-card-inner {
  position: relative;
  background: var(--bg-card, #11243d);
  border: 1px solid var(--border);
  border-radius: 20px;
  padding: 22px 20px 18px;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 14px;
  overflow: hidden;
  transition: all 0.35s ease;
}

.home-card:hover .home-card-inner {
  border-color: transparent;
  box-shadow:
    0 12px 32px rgba(0, 0, 0, 0.12),
    0 0 0 1px rgba(255, 255, 255, 0.05);
}

/* Accent bar on top */
.card-accent-bar {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  border-radius: 0 0 3px 3px;
  opacity: 0.7;
  transition: opacity 0.3s, height 0.3s;
}

.home-card:hover .card-accent-bar {
  opacity: 1;
  height: 4px;
}

/* Accent Colors */
.card-accent-blue .card-accent-bar { background: linear-gradient(90deg, #3b82f6, #60a5fa); }
.card-accent-cyan .card-accent-bar { background: linear-gradient(90deg, #06b6d4, #22d3ee); }
.card-accent-indigo .card-accent-bar { background: linear-gradient(90deg, #6366f1, #818cf8); }
.card-accent-violet .card-accent-bar { background: linear-gradient(90deg, #8b5cf6, #a78bfa); }
.card-accent-rose .card-accent-bar { background: linear-gradient(90deg, #f43f5e, #fb7185); }
.card-accent-amber .card-accent-bar { background: linear-gradient(90deg, #f59e0b, #fbbf24); }
.card-accent-green .card-accent-bar { background: linear-gradient(90deg, #10b981, #34d399); }
.card-accent-teal .card-accent-bar { background: linear-gradient(90deg, #14b8a6, #2dd4bf); }
.card-accent-orange .card-accent-bar { background: linear-gradient(90deg, #f97316, #fb923c); }

/* Icon bg based on accent */
.card-accent-blue .card-icon-box { background: rgba(59, 130, 246, 0.12); }
.card-accent-cyan .card-icon-box { background: rgba(6, 182, 212, 0.12); }
.card-accent-indigo .card-icon-box { background: rgba(99, 102, 241, 0.12); }
.card-accent-violet .card-icon-box { background: rgba(139, 92, 246, 0.12); }
.card-accent-rose .card-icon-box { background: rgba(244, 63, 94, 0.12); }
.card-accent-amber .card-icon-box { background: rgba(245, 158, 11, 0.12); }
.card-accent-green .card-icon-box { background: rgba(16, 185, 129, 0.12); }
.card-accent-teal .card-icon-box { background: rgba(20, 184, 166, 0.12); }
.card-accent-orange .card-icon-box { background: rgba(249, 115, 22, 0.12); }

/* Hover glow per accent */
.card-accent-blue:hover .home-card-inner { box-shadow: 0 12px 32px rgba(59, 130, 246, 0.12), 0 0 0 1px rgba(59, 130, 246, 0.15); }
.card-accent-cyan:hover .home-card-inner { box-shadow: 0 12px 32px rgba(6, 182, 212, 0.12), 0 0 0 1px rgba(6, 182, 212, 0.15); }
.card-accent-indigo:hover .home-card-inner { box-shadow: 0 12px 32px rgba(99, 102, 241, 0.12), 0 0 0 1px rgba(99, 102, 241, 0.15); }
.card-accent-violet:hover .home-card-inner { box-shadow: 0 12px 32px rgba(139, 92, 246, 0.12), 0 0 0 1px rgba(139, 92, 246, 0.15); }
.card-accent-rose:hover .home-card-inner { box-shadow: 0 12px 32px rgba(244, 63, 94, 0.12), 0 0 0 1px rgba(244, 63, 94, 0.15); }
.card-accent-amber:hover .home-card-inner { box-shadow: 0 12px 32px rgba(245, 158, 11, 0.12), 0 0 0 1px rgba(245, 158, 11, 0.15); }
.card-accent-green:hover .home-card-inner { box-shadow: 0 12px 32px rgba(16, 185, 129, 0.12), 0 0 0 1px rgba(16, 185, 129, 0.15); }
.card-accent-teal:hover .home-card-inner { box-shadow: 0 12px 32px rgba(20, 184, 166, 0.12), 0 0 0 1px rgba(20, 184, 166, 0.15); }
.card-accent-orange:hover .home-card-inner { box-shadow: 0 12px 32px rgba(249, 115, 22, 0.12), 0 0 0 1px rgba(249, 115, 22, 0.15); }

/* Card Top */
.card-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
}

.card-icon-box {
  width: 52px;
  height: 52px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: transform 0.3s ease;
}

.home-card:hover .card-icon-box {
  transform: scale(1.08);
}

.card-svg-icon {
  font-size: 24px;
  color: var(--text);
}

/* Icon color per accent */
.card-accent-blue .card-svg-icon { color: #3b82f6; }
.card-accent-cyan .card-svg-icon { color: #06b6d4; }
.card-accent-indigo .card-svg-icon { color: #6366f1; }
.card-accent-violet .card-svg-icon { color: #8b5cf6; }
.card-accent-rose .card-svg-icon { color: #f43f5e; }
.card-accent-amber .card-svg-icon { color: #f59e0b; }
.card-accent-green .card-svg-icon { color: #10b981; }
.card-accent-teal .card-svg-icon { color: #14b8a6; }
.card-accent-orange .card-svg-icon { color: #f97316; }

.card-badge {
  font-size: 10px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 20px;
  white-space: nowrap;
  letter-spacing: 0.3px;
}

.badge-red { background: rgba(239, 68, 68, 0.12); color: #ef4444; }
.badge-orange { background: rgba(245, 158, 11, 0.12); color: #f59e0b; }
.badge-green { background: rgba(34, 197, 94, 0.12); color: #22c55e; }
.badge-blue { background: rgba(59, 130, 246, 0.12); color: #3b82f6; }

/* Card Body */
.card-body {
  flex: 1;
}

.card-title {
  margin: 0 0 4px;
  font-size: 16px;
  font-weight: 700;
  color: var(--text);
  line-height: 1.3;
  transition: color 0.2s;
}

.card-desc {
  margin: 0;
  font-size: 12px;
  color: var(--text-soft);
  line-height: 1.5;
}

/* Card Footer */
.card-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 12px;
  border-top: 1px solid var(--border);
  margin-top: auto;
}

.card-link-text {
  font-size: 12px;
  font-weight: 600;
  color: var(--text-soft);
  letter-spacing: 0.3px;
  transition: color 0.2s;
}

.card-arrow {
  font-size: 16px;
  color: var(--text-soft);
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.04);
}

.home-card:hover .card-link-text {
  color: var(--primary);
}

.home-card:hover .card-arrow {
  color: var(--primary);
  background: rgba(59, 130, 246, 0.1);
  transform: translateX(3px);
}

/* Disabled state */
.home-card.is-disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.home-card.is-disabled:hover {
  transform: none;
}

.home-card.is-disabled:hover .home-card-inner {
  border-color: var(--border);
  box-shadow: none;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* ===== Animations ===== */
@keyframes cardFadeIn {
  from {
    opacity: 0;
    transform: translateY(14px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ===== Tablet ===== */
@media (max-width: 1024px) {
  .page-content {
    padding: 0 20px 28px;
  }

  .categories-grid {
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
  }

  .section-title {
    font-size: 22px;
  }

  .card-icon-box {
    width: 46px;
    height: 46px;
  }

  .card-svg-icon {
    font-size: 22px;
  }
}

/* ===== Mobile ===== */
@media (max-width: 768px) {
  .page-content {
    padding: 0 16px 24px;
  }

  .categories-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 14px;
  }

  .section-title {
    font-size: 20px;
  }

  .section-subtitle {
    font-size: 13px;
  }

  .premium-banner-content {
    flex-direction: column;
    text-align: center;
  }

  .home-card-inner {
    padding: 16px 14px 14px;
    gap: 10px;
  }

  .card-icon-box {
    width: 42px;
    height: 42px;
    border-radius: 12px;
  }

  .card-svg-icon {
    font-size: 20px;
  }

  .card-title {
    font-size: 14px;
  }

  .card-desc {
    font-size: 11px;
  }

  .card-footer {
    padding-top: 8px;
  }
}

/* ===== Small Mobile ===== */
@media (max-width: 576px) {
  .page-content {
    padding: 0 12px 20px;
  }

  .categories-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .section-title {
    font-size: 18px;
  }

  .home-card-inner {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    padding: 16px;
    gap: 12px;
  }

  .card-accent-bar {
    width: 3px;
    height: 100%;
    top: 0;
    left: 0;
    right: auto;
    bottom: 0;
    border-radius: 0 3px 3px 0;
  }

  .home-card:hover .card-accent-bar {
    height: 100%;
    width: 4px;
  }

  .card-top {
    flex-shrink: 0;
  }

  .card-badge {
    position: absolute;
    top: 8px;
    right: 12px;
  }

  .card-body {
    flex: 1;
    min-width: 0;
  }

  .card-footer {
    display: none;
  }
}
</style>